// A device stays unlocked after the passphrase and authenticator code until
// it goes 5 days without the app being opened, then asks for both again:
// each open the key is recalled with starts the 5 days over. The key is kept as a non-extractable
// CryptoKey in IndexedDB: the page can use it, but not read its bytes out.
// Next to it, the id of the device's passkey (./biometric.ts), which outlives
// the key: it belongs to the device, not to one unlock.

import type { Keycheck } from './crypto';

const DB = 'notes-session';
const STORE = 'session';
const ID = 'current';
const PASSKEY = 'passkey';
export const REMEMBER_FOR = 5 * 24 * 60 * 60 * 1000;

interface Remembered {
  key: CryptoKey;
  // the keycheck `check` it was unlocked against, so a key for another repo
  // or an old passphrase is never used
  check: string;
  expiresAt: number;
}

const open = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const run = async <T>(
  mode: IDBTransactionMode,
  op: (store: IDBObjectStore) => IDBRequest<T>,
) => {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = op(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
};

const keep = (key: CryptoKey, check: string) =>
  run('readwrite', store =>
    store.put(
      {
        key,
        check,
        expiresAt: Date.now() + REMEMBER_FOR,
      } satisfies Remembered,
      ID,
    ));

export const remember = (key: CryptoKey, keycheck: Keycheck) =>
  keep(key, keycheck.check);

export const forget = () => run('readwrite', store => store.delete(ID));

// the remembered key, its 5 days started over, or null once it has expired
// or stopped matching
export const recall = async (keycheck: Keycheck | null) => {
  const found = await run<Remembered | undefined>(
    'readonly',
    store => store.get(ID),
  );
  if (!found) return null;
  if (found.expiresAt > Date.now() && found.check === keycheck?.check) {
    await keep(found.key, found.check);
    return found.key;
  }
  await forget();
  return null;
};

export const savedPasskey = () =>
  run<string | undefined>('readonly', store => store.get(PASSKEY))
    .then(id => id ?? null);

export const savePasskey = (id: string) =>
  run('readwrite', store => store.put(id, PASSKEY));

export const dropPasskey = () =>
  run('readwrite', store => store.delete(PASSKEY));
